import type { EAnalyticMode } from '~/core/gemxql';
import type { IOrder } from '../../types';

import { ORDER_LISTING_REQUIRED_METRICS } from '../../constants';
import { useOrderListingData } from '../useOrderListingData';
import { useStoreTotalOrders } from './useStoreTotalOrders';
import { useOrderParameters } from './useOrderParameters';
import { useOrderControlStore } from '../../stores';
import { useQueryOrderListing } from '../../api';
import { useMemo, useCallback } from 'react';

interface IUseFetchOrderListingProps {
  mode: EAnalyticMode;
  limit: number;
  offset: number;
  queryValue?: string;
}

interface IUseFetchOrderListingReturn {
  totalStoreOrders: number;
  totalFilteredOrders: number;
  orderListing: IOrder[];
  isLoading: boolean;
  isFetching: boolean;
  refetchOrderListing: () => void;
}

export const useFetchOrderListing = ({
  mode,
  limit,
  offset,
  queryValue,
}: IUseFetchOrderListingProps): IUseFetchOrderListingReturn => {
  const { showColumns } = useOrderListingData({});
  const { totalStoreOrders, refetchStoreTotalOrders } = useStoreTotalOrders({ mode });

  const orderControlData = useOrderControlStore((state) => state.orderControlData);
  const isReady = orderControlData !== undefined;

  const orderListingMetrics = useMemo(() => {
    const columnMetrics = showColumns.map((column) => column.name);
    const missingRequiredMetrics = ORDER_LISTING_REQUIRED_METRICS.filter(
      (metric) => !columnMetrics.includes(metric),
    );

    return [...columnMetrics, ...missingRequiredMetrics];
  }, [showColumns]);

  const { parameters: parametersForFilteredTotal } = useOrderParameters({
    metrics: ['orders'],
    mode,
    isSyncSearchParams: false,
    queryValue,
  });

  const { total: totalFilteredOrders } = useQueryOrderListing({
    variables: parametersForFilteredTotal,
    enabled: isReady,
    name: 'OrderListingFilteredTotalQL',
  });

  const { parameters: parametersForOrderListing } = useOrderParameters({
    metrics: orderListingMetrics,
    mode,
    limit,
    offset,
    queryValue,
  });

  const {
    data: orderListing,
    isLoading,
    isFetching,
    refetch: refetchListing,
  } = useQueryOrderListing({
    variables: parametersForOrderListing,
    enabled: isReady,
    name: 'OrderListingQL',
  });

  const refetchOrderListing = useCallback(() => {
    refetchStoreTotalOrders();
    refetchListing();
  }, [refetchStoreTotalOrders, refetchListing]);

  return {
    totalStoreOrders,
    totalFilteredOrders,
    orderListing,
    isLoading,
    isFetching,
    refetchOrderListing,
  };
};
